// ✅ PHASE 4: Firebase Configuration for Push Notifications (FCM)
// According to PROFESSIONAL_REFACTORING_PLAN.md lines 496-499

import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getMessaging, getToken, onMessage, type Messaging } from 'firebase/messaging'
import { logger } from '@/utils/logger'

// Firebase configuration from environment variables
// To set up:
// 1. Create Firebase project at https://console.firebase.google.com/
// 2. Enable Cloud Messaging (FCM)
// 3. Add web app to get config
// 4. Add these values to env.development:
//    VITE_FIREBASE_API_KEY=...
//    VITE_FIREBASE_AUTH_DOMAIN=...
//    VITE_FIREBASE_PROJECT_ID=...
//    VITE_FIREBASE_STORAGE_BUCKET=...
//    VITE_FIREBASE_MESSAGING_SENDER_ID=...
//    VITE_FIREBASE_APP_ID=...
//    VITE_FIREBASE_VAPID_KEY=... (from Cloud Messaging > Web Push certificates)

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

let app: FirebaseApp | null = null
let messaging: Messaging | null = null

/**
 * Initialize Firebase app
 * Only initializes if config is present (production/staging)
 */
export const initializeFirebase = (): FirebaseApp | null => {
  try {
    // Skip if already initialized
    if (app) return app

    // Skip if no config (development without Firebase)
    if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
      logger.info('Firebase config not found, push notifications disabled')
      return null
    }

    app = initializeApp(firebaseConfig)
    logger.info('Firebase initialized successfully', {
      action: 'firebase_init',
      metadata: { projectId: firebaseConfig.projectId }
    })

    return app
  } catch (error) {
    logger.error('Failed to initialize Firebase', {
      action: 'firebase_init_error',
      error: error instanceof Error ? error.message : error
    })
    return null
  }
}

/**
 * Get Firebase Cloud Messaging instance
 */
export const getFirebaseMessaging = (): Messaging | null => {
  try {
    // Initialize app if not already done
    if (!app) {
      initializeFirebase()
    }

    // Skip if app failed to initialize
    if (!app) return null

    // Skip if already initialized
    if (messaging) return messaging

    messaging = getMessaging(app)
    logger.info('Firebase Messaging initialized', {
      action: 'fcm_init'
    })

    return messaging
  } catch (error) {
    logger.error('Failed to get Firebase Messaging', {
      action: 'fcm_init_error',
      error: error instanceof Error ? error.message : error
    })
    return null
  }
}

/**
 * Request notification permission and get FCM token
 * @returns FCM token if successful, null otherwise
 */
export const requestNotificationPermission = async (): Promise<string | null> => {
  try {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      logger.warn('Notifications not supported in this browser')
      return null
    }

    // Request permission
    const permission = await Notification.requestPermission()

    if (permission !== 'granted') {
      logger.info('Notification permission denied', {
        action: 'notification_permission',
        metadata: { permission }
      })
      return null
    }

    // Get messaging instance
    const fcmMessaging = getFirebaseMessaging()
    if (!fcmMessaging) {
      logger.warn('FCM not available, cannot get token')
      return null
    }

    // Get VAPID key from environment
    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY
    if (!vapidKey) {
      logger.error('VITE_FIREBASE_VAPID_KEY not configured')
      return null
    }

    // Get FCM token
    const token = await getToken(fcmMessaging, { vapidKey })

    if (!token) {
      logger.warn('Failed to get FCM token')
      return null
    }

    logger.info('FCM token obtained successfully', {
      action: 'fcm_token_obtained',
      metadata: { tokenLength: token.length }
    })

    return token
  } catch (error) {
    logger.error('Failed to request notification permission', {
      action: 'notification_permission_error',
      error: error instanceof Error ? error.message : error
    })
    return null
  }
}

/**
 * Set up foreground message handler
 * Called when a notification arrives while app is in foreground
 */
export const setupForegroundMessageHandler = (
  onMessageReceived: (payload: any) => void
): (() => void) | null => {
  try {
    const fcmMessaging = getFirebaseMessaging()
    if (!fcmMessaging) {
      logger.warn('FCM not available, cannot set up message handler')
      return null
    }

    // Set up onMessage listener
    const unsubscribe = onMessage(fcmMessaging, (payload) => {
      logger.info('Foreground message received', {
        action: 'fcm_message_received',
        metadata: {
          title: payload.notification?.title,
          hasData: !!payload.data
        }
      })

      onMessageReceived(payload)
    })

    logger.info('Foreground message handler set up', {
      action: 'fcm_handler_setup'
    })

    return unsubscribe
  } catch (error) {
    logger.error('Failed to set up foreground message handler', {
      action: 'fcm_handler_setup_error',
      error: error instanceof Error ? error.message : error
    })
    return null
  }
}

/**
 * Check if FCM is configured and available
 */
export const isFCMAvailable = (): boolean => {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    import.meta.env.VITE_FIREBASE_VAPID_KEY &&
    'Notification' in window
  )
}

